import { StatusQuestionConfig, StatusResponseState } from '@gripp/shared-logic';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, Text, View } from 'react-native';
import { colors, spacing, tokens } from '../../../../crop-circle/tokens';
import { ControlledTextInput, useFormContext } from '../../../../forms';
import { OpenEndedResponse } from '../../../status/types';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../types';
import { themedStyleSheet } from '../../../../../themes/themedStyleSheet';

export const OpenEndedResponseExecutionComponent: RoutineQuestionExecutionForm<
  StatusQuestionConfig,
  StatusResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    StatusQuestionConfig,
    StatusResponseState
  >
) => {
  const fieldPrefix = `responses.${props.responseIndex}`;
  const openEndedResponseKey = `${fieldPrefix}.openEndedResponse`;
  const statusKey = `${fieldPrefix}.status`;
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const selectedStatus = useWatch({
    control,
    name: statusKey,
  });
  const isSelectedOpenEndedResponse = useMemo(
    () => selectedStatus === OpenEndedResponse,
    [selectedStatus]
  );

  useEffect(() => {
    if (
      (props.currentState?.state?.responses?.[props.responseIndex]?.status ===
        OpenEndedResponse ||
        props.currentState?.state?.status === OpenEndedResponse) &&
      props?.config?.openEndedResponse
    ) {
      const openEndedResponse =
        props.currentState?.state?.responses?.[props.responseIndex]
          ?.openEndedResponse ?? props.currentState?.state?.openEndedResponse;
      setValue(openEndedResponseKey, openEndedResponse, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(statusKey, OpenEndedResponse, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [props.currentState]);

  if (!props.config.openEndedResponse) return null;

  const onStatusChange = (status: string) => {
    setValue(statusKey, status, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <View style={styles.container}>
      <Pressable
        style={[
          styles.button,
          isSelectedOpenEndedResponse && styles.currentStateButton,
        ]}
        onPress={() => {
          onStatusChange(OpenEndedResponse);
        }}
      >
        {isSelectedOpenEndedResponse ? (
          <ControlledTextInput
            control={control}
            name={openEndedResponseKey}
            placeholder=""
            width={'100%'}
            textColor={colors.brand.white}
            backgroundColor={'transparent'}
            hideBorder
          />
        ) : (
          <Text style={styles.buttonLabel}>
            {t('routines.status.admin.otherStatus')}
          </Text>
        )}
      </Pressable>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    marginTop: spacing.sm,
  },
  button: {
    height: 52,
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: role.surfaceInverseSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  currentStateButton: {
    backgroundColor: role.surfaceInverse,
  },
  buttonLabel: {
    ...tokens.typography.body.medium,
    color: role.textPrimary,
  },
}));
